import { z } from 'zod';
/**
 * Using a mailbox, as opposed to running one.
 *
 * `mail.ts` beside this describes the business of hosting email — domains,
 * DNS, seats, who is allowed how many. This file is the other half: the
 * person who was given one of those addresses, reading it and replying to it
 * without leaving the app. The two are deliberately separate because they are
 * used by different people on different days, and because nothing here should
 * ever need a domain's DKIM key or a workspace's seat count.
 *
 * Everything below crosses the wire from an IMAP server the app talks to on
 * the reader's behalf, so two things are true of every shape here:
 *
 * - **A message is not our data.** Every string in it was written by whoever
 *   sent it. Subjects, names and HTML bodies are hostile until proven
 *   otherwise; the server sanitises the body before it is ever serialised.
 * - **A message is addressed by folder and UID, never by an id we invented.**
 *   IMAP UIDs are unique only within one folder, so the folder travels with
 *   the UID everywhere, including in the URLs.
 */
/* -------------------------------- folders -------------------------------- */
/**
 * What a folder is *for*, as distinct from what it is called.
 *
 * IMAP servers name these differently in every locale and every product —
 * `Sent`, `Sent Items`, `Éléments envoyés` — but they advertise the purpose as
 * a special-use flag. Reading the flag rather than the name is what lets the
 * app put a reply in the right place on a server it has never seen.
 */
export const MAIL_FOLDER_ROLES = [
    'inbox',
    'sent',
    'drafts',
    'trash',
    'junk',
    'archive',
    'other',
];
/* -------------------------------- schemas -------------------------------- */
/**
 * Connecting a mailbox: the one place a mailbox password is accepted from a
 * person for any purpose other than being hashed.
 *
 * It is checked against the stored hash first and sealed with the vault key
 * before it is written, so a stolen database is not a stolen mailbox. It is
 * still a real widening of what the module holds, which is why it is opt-in
 * per mailbox and undoable from the same screen.
 */
export const mailConnectSchema = z.object({
    password: z.string().min(1).max(200),
});
const sendAddressSchema = z
    .string()
    .transform((value) => value.trim())
    .pipe(z.string().email('Check the address'));
export const mailSendSchema = z
    .object({
    to: z.array(sendAddressSchema).min(1, 'Add at least one recipient').max(50),
    cc: z.array(sendAddressSchema).max(50).default([]),
    bcc: z.array(sendAddressSchema).max(50).default([]),
    subject: z.string().trim().max(500).default(''),
    /** Composed as plain text. The HTML part is generated from it on the server. */
    text: z.string().max(200_000).default(''),
    /** The message being answered, so the reply threads and the original is flagged. */
    replyTo: z
        .object({ folder: z.string().min(1).max(500), uid: z.number().int().positive() })
        .optional(),
})
    .refine((value) => value.subject.trim().length > 0 || value.text.trim().length > 0, 'Write a subject or a message');
export const mailFlagSchema = z.object({
    seen: z.boolean().optional(),
    flagged: z.boolean().optional(),
});
export const mailMoveSchema = z.object({
    /** The destination folder's path, as listed. */
    to: z.string().min(1).max(500),
});
/* -------------------------------- helpers -------------------------------- */
/** `Maya Chen <maya@acme.com>` when there is a name, the address alone when not. */
export function formatMailAddress(value) {
    if (!value)
        return '';
    return value.name ? `${value.name} <${value.address}>` : value.address;
}
/** What to show in a list: the name if we have one, otherwise the address. */
export function mailAddressLabel(value) {
    if (!value)
        return 'Unknown sender';
    return value.name || value.address;
}
/**
 * The subject line for a reply, without stacking `Re:` forever.
 *
 * Only the English prefix is stripped, and deliberately so — guessing at every
 * locale's abbreviation is how `Re: Re: AW: SV:` happens.
 */
export function replySubject(subject) {
    const trimmed = subject.trim();
    if (/^re:/i.test(trimmed))
        return trimmed;
    return `Re: ${trimmed}`;
}
/**
 * The quoted original under a reply.
 *
 * Plain text, `>`-prefixed, which is what every mail client since 1980 has
 * understood and what the ones that do not still render harmlessly.
 */
export function quoteForReply(message) {
    const when = new Date(message.date);
    const stamp = Number.isNaN(when.getTime()) ? '' : when.toLocaleString();
    const who = formatMailAddress(message.from) || 'someone';
    const body = message.text
        .split('\n')
        .slice(0, 200)
        .map((line) => `> ${line}`)
        .join('\n');
    return `\n\nOn ${stamp}, ${who} wrote:\n${body}\n`;
}
